import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Col, Collapse, Container, Form, Row } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faFileCsv, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import Input from '../../../components/Input';
import InputV2 from '../../../components/Systema/Input';
import Service from '../../../components/Systema/Service';
import config from '../../../config';
import Totalizadores from './tabs/Totalizadores';
import RelacaoEmendasTab from './tabs/RelacaoEmendasTab';
import ReceitasTab from './tabs/ReceitasTab';
import ExecucaoDespesaTab from './tabs/ExecucaoDespesaTab';
import PagamentosTab from './tabs/PagamentosTab';
import {
  formatDateBR,
  formatPessoaJuridica,
  getAcaoGovernamental,
  getUnidadeJurisdicionada,
  getSituacao,
  getValorArrecadado,
} from './emendaUtils';
import { exportToCsv, exportReportToCsv, exportToTxt, exportReportToTxt } from '../../../utils/exportCsv';
import './styles.scss';
import AjustesTab from './tabs/AjustesTab';
import LiquidacaoTab from './tabs/LiquidacaoTab';

const tabs = [
  { key: 'relacao', label: 'Emendas Parlamentares' },
  { key: 'receitas', label: 'Receita (Recursos Recebidos)' },
  { key: 'execucao', label: 'Empenhos' },
  { key: 'ajustes', label: 'Anulações/Reforços' },
  { key: 'liquidacao', label: 'Liquidações/Estorno' },
  { key: 'pagamentos', label: 'Pagamentos/Estorno' },
];

const filtrosIniciais = {
  unidadeJurisdicionada: null,
  exercicio: '',
  tipo: null,
  modalidadeExecucao: null,
  numero: '',
  autoria: '',
  objeto: '',
  beneficiarioFinal: null,
  situacao: null,
};

const getNomeFiltro = (value) => (value && value.nome ? value.nome : '');
const filtrosAplicadosIniciais = {
  ...filtrosIniciais,
  unidadeJurisdicionada: '',
  tipo: '',
  tipoId: '',
  modalidadeExecucao: '',
  modalidadeExecucaoId: '',
  beneficiarioFinal: '',
  beneficiarioFinalId: '',
  situacao: '',
  situacaoId: '',
};

export default function RastreabilidadeEmendaParlamentarPage() {
  const [showFiltro, setShowFiltro] = useState(false);
  const [activeTab, setActiveTab] = useState('relacao');
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosAplicadosIniciais);
  const [emendasTotalizadores, setEmendasTotalizadores] = useState([]);
  const [tabReports, setTabReports] = useState({});
  const [visitedTabs, setVisitedTabs] = useState({ relacao: true });

  const location = useLocation();
  const params = useParams();
  const queryParams = new URLSearchParams(location.search);
  const identificador =
    queryParams.get('id') ||
    queryParams.get('identificador') ||
    queryParams.get('portal') ||
    params.identificador ||
    params.cliente;

  const service = useMemo(() => {
    if (!identificador) return null;
    return new Service(`${config.api_path}/public/v1/transparencia/emenda-parlamentar/${identificador}`);
  }, [identificador]);

  const getPublicPath = (path) => (
    identificador
      ? `${config.api_path}/public/v1/transparencia/${path}/${identificador}`
      : `${config.api_path}/public/v1/transparencia/${path}`
  );
  // const unidadeJurisdicionadaPath = `${config.api_path}/public/v1/clientesiorconp/${identificador}`;
  const UnidadeJurisdicionadaService = useMemo(() => {
    if (!identificador) return null;
    return new Service(`${config.api_path}/public/v1/clientesiorconp/${identificador}`);
  }, [identificador]);

  const handleFiltroChange = (event) => {
    const { id, value } = event.target;
    setFiltros((prev) => ({ ...prev, [id]: value }));
  };

  const handleBuscar = (event) => {
    event.preventDefault();
    setPage(1);
    setFiltrosAplicados({
      ...filtros,
      unidadeJurisdicionada: getNomeFiltro(filtros.unidadeJurisdicionada),
      tipo: getNomeFiltro(filtros.tipo),
      tipoId: filtros.tipo && filtros.tipo.id ? filtros.tipo.id : '',
      modalidadeExecucao: getNomeFiltro(filtros.modalidadeExecucao),
      modalidadeExecucaoId: filtros.modalidadeExecucao && filtros.modalidadeExecucao.id ? filtros.modalidadeExecucao.id : '',
      beneficiarioFinal: getNomeFiltro(filtros.beneficiarioFinal),
      beneficiarioFinalId: filtros.beneficiarioFinal && filtros.beneficiarioFinal.id ? filtros.beneficiarioFinal.id : '',
      situacao: getNomeFiltro(filtros.situacao),
      situacaoId: filtros.situacao && filtros.situacao.id ? filtros.situacao.id : '',
    });
  };

  const limparFiltros = () => {
    setFiltros(filtrosIniciais);
    setFiltrosAplicados(filtrosAplicadosIniciais);
    setPage(1);
  };

  const handleTotalizadoresChange = useCallback((emendas) => {
    setEmendasTotalizadores(emendas);
  }, []);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setVisitedTabs((prev) => (prev[tabKey] ? prev : { ...prev, [tabKey]: true }));
  };

  const handleTabReportChange = useCallback((tabKey, report) => {
    setTabReports((prev) => ({ ...prev, [tabKey]: report }));
  }, []);

  const buildRelacaoRows = () => emendasTotalizadores.map((row) => [
    getUnidadeJurisdicionada(row),
    row.exercicio || '',
    (row.emendaParlamentarTipo && row.emendaParlamentarTipo.nome) || '',
    (row.emendaParlamentarModalidadeExecucao && row.emendaParlamentarModalidadeExecucao.nome) || row.modalidadeExecucao || '',
    
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.unidadeOrcamentaria : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.funcao : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.subfuncao : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.programa : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.acao : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.despesa : '-'),
    (row.classificacaoOrcamentaria ? row.classificacaoOrcamentaria.fonteRecurso : '-'),

    row.numeroEmenda || '',
    row.autoria || '',
    formatPessoaJuridica(row.emendaParlamentarConcedente),
    formatPessoaJuridica(row.emendaParlamentarBeneficiario),
    row.detalhamentoObjeto || '',
    row.numeroProposta || '',
    row.contaParaDeposito || '',
    row.atoNormativoAprovacao || '',
    formatDateBR(row.dataDaEmenda),
    getSituacao(row),
    row.observacao || '',
    row.valorPrevisto != null ? row.valorPrevisto : '',
    row.valorAprovado != null ? row.valorAprovado : '',
    getValorArrecadado(row) != null ? getValorArrecadado(row) : '',
    row.totalEmpenhado != null ? row.totalEmpenhado : '',
    row.totalLiquidado != null ? row.totalLiquidado : '',
    row.totalPago != null ? row.totalPago : '',
  ]);

  const handleExportCsv = () => {
    if (activeTab === 'relacao') {
      const headers = [
        'Unidade Jurisdicionada', 'Exercício', 'Tipo', 'Modalidade de Execução',
        'Unidade Orçamentária', 'Função', 'Subfunção', 'Programa', 'Ação Governamental', 'Despesa', 'Fonte de Recurso',
        'Número da Emenda', 'Autor da Emenda', 'Órgão ou Entidade Concedente',
        'Órgão ou Entidade Beneficiária', 'Detalhamento do Objeto', 'Número da Proposta',
        'Conta para Depósito', 'Ato Normativo que Aprovou a Emenda', 'Data da Aprovação da Emenda',
        'Situação da Emenda', 'Observações / Motivos de Impedimentos', 'Valor da Proposta',
        'Valor Aprovado', 'Valor Arrecadado', 'Valor Empenhado', 'Valor Liquidado', 'Valor Pago',
      ];
      const rows = buildRelacaoRows();
      exportToCsv('emendas-parlamentares.csv', headers, rows);
    } else if (activeTab === 'receitas') {
      exportReportToCsv('emendas-receitas.csv', tabReports.receitas);
    } else if (activeTab === 'execucao') {
      exportReportToCsv('emendas-execucao-despesa.csv', tabReports.execucao);
    } else if (activeTab === 'ajustes') {
      exportReportToCsv('emendas-ajustes.csv', tabReports.ajustes);
    } else if (activeTab === 'liquidacao') {
      exportReportToCsv('emendas-liquidacao.csv', tabReports.liquidacao);
    } else if (activeTab === 'pagamentos') {
      exportReportToCsv('emendas-pagamentos.csv', tabReports.pagamentos);
    }
  };

  const handleExportTxt = () => {
    if (activeTab === 'relacao') {
      const headers = [
        'Unidade Jurisdicionada', 'Exercício', 'Tipo', 'Modalidade de Execução',
        'Unidade Orçamentária', 'Função', 'Subfunção', 'Programa', 'Ação Governamental', 'Despesa', 'Fonte de Recurso',
        'Número da Emenda', 'Autor da Emenda', 'Órgão ou Entidade Concedente',
        'Órgão ou Entidade Beneficiária', 'Detalhamento do Objeto', 'Número da Proposta',
        'Conta para Depósito', 'Ato Normativo que Aprovou a Emenda', 'Data da Aprovação da Emenda',
        'Situação da Emenda', 'Observações / Motivos de Impedimentos', 'Valor da Proposta',
        'Valor Aprovado', 'Valor Arrecadado', 'Valor Empenhado', 'Valor Liquidado', 'Valor Pago',
      ];
      const rows = buildRelacaoRows();
      exportToTxt('emendas-parlamentares.txt', headers, rows);
    } else if (activeTab === 'receitas') {
      exportReportToTxt('emendas-receitas.txt', tabReports.receitas);
    } else if (activeTab === 'execucao') {
      exportReportToTxt('emendas-execucao-despesa.txt', tabReports.execucao);
    } else if (activeTab === 'ajustes') {
      exportReportToTxt('emendas-ajustes.txt', tabReports.ajustes);
    } else if (activeTab === 'liquidacao') {
      exportReportToTxt('emendas-liquidacao.txt', tabReports.liquidacao);
    } else if (activeTab === 'pagamentos') {
      exportReportToTxt('emendas-pagamentos.txt', tabReports.pagamentos);
    }
  };

  return (
    <Container fluid>
      <Card className='mt-4 mb-2'>
        <Card.Body>
          <div className='d-flex justify-content-between'>
            <div>
              <h3 className='font-weight-light my-0'>Rastreabilidade das Emendas Parlamentares</h3>
            </div>
            <span className='align-items-center emenda-fonte-dados'>
              <small className='font-weight-bold'>Fonte de Dados:</small>
              <small className='text-right'>SIORCONP</small>
              <small className='font-weight-bold'>Ultima Atualização:</small>
              <small className='text-right'>{new Date().toLocaleString()}</small>
            </span>
          </div>
        </Card.Body>
      </Card>

      <Card className='my-2'>
        <Card.Header className='py-2' onClick={() => setShowFiltro(!showFiltro)} style={{ cursor: 'pointer' }}>
          <div className='d-flex justify-content-between'>
            <div>
              <Card.Title className='my-0'>Filtros</Card.Title>
              {!showFiltro && <small className='text-muted'>Unidade Jurisdicionada, exercício, tipo, modalidade de execução, número, autoria, objeto, beneficiário final e situação.</small>}
            </div>
            <div>
              <FontAwesomeIcon icon={showFiltro ? faMinus : faPlus} />
            </div>
          </div>
        </Card.Header>
        <Collapse in={showFiltro}>
          <div>
            <Card.Body>
              <Form onSubmit={handleBuscar}>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Unidade Jurisdicionada</Form.Label>
                      <InputV2.AsyncSelect nullable
                        id='unidadeJurisdicionada'
                        value={filtros.unidadeJurisdicionada}
                        format={item => item && item.nome}
                        placeholder="Todas"
                        onChange={handleFiltroChange}
                        service={UnidadeJurisdicionadaService}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Exercício</Form.Label>
                      <Form.Control id='exercicio' type='number' value={filtros.exercicio} onChange={handleFiltroChange} placeholder='Ano' />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Tipo</Form.Label>
                      <Input.AsyncSelect
                        id='tipo'
                        value={filtros.tipo}
                        onChange={handleFiltroChange}
                        path={getPublicPath('emenda-parlamentar-tipo')}
                        label={'${nome}'}
                        rowKey='id'
                        nullable
                        placeholder='Todos'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Modalidade de execução</Form.Label>
                      <Input.AsyncSelect
                        id='modalidadeExecucao'
                        value={filtros.modalidadeExecucao}
                        onChange={handleFiltroChange}
                        path={getPublicPath('emenda-parlamentar-modalidade-execucao')}
                        label={'${nome}'}
                        rowKey='id'
                        nullable
                        placeholder='Todas'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Número</Form.Label>
                      <Form.Control id='numero' value={filtros.numero} onChange={handleFiltroChange} placeholder='Número' />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Situação</Form.Label>
                      <Input.AsyncSelect
                        id='situacao'
                        value={filtros.situacao}
                        onChange={handleFiltroChange}
                        path={getPublicPath('emenda-parlamentar-situacao')}
                        label={'${nome}'}
                        rowKey='id'
                        nullable
                        placeholder='Todas'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Autoria</Form.Label>
                      <Form.Control id='autoria' value={filtros.autoria} onChange={handleFiltroChange} placeholder='Autor da emenda' />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Objeto</Form.Label>
                      <Form.Control id='objeto' value={filtros.objeto} onChange={handleFiltroChange} placeholder='Objeto da emenda' />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Beneficiário Final</Form.Label>
                      <Input.AsyncSelect
                        id='beneficiarioFinal'
                        value={filtros.beneficiarioFinal}
                        onChange={handleFiltroChange}
                        path={getPublicPath('emenda-parlamentar-beneficiario')}
                        label={'${nome}'}
                        rowKey='id'
                        nullable
                        placeholder='Todos'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className='d-flex align-items-end'>
                    <div className='emenda-filter-actions'>
                      <Button variant='outline-primary' type='submit'>
                        Buscar
                      </Button>
                      <Button variant='outline-secondary' type='button' onClick={limparFiltros}>
                        Limpar
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Totalizadores emendas={emendasTotalizadores} />

      <div className='emenda-tabs my-2'>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type='button'
            className={`emenda-tab h-auto ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className='emenda-relatorios'>
        <span className='emenda-relatorios-label'>Relatórios</span>
        <button type='button' className='emenda-relatorios-btn' onClick={handleExportCsv}>
          <FontAwesomeIcon icon={faFileCsv} className='mr-1' />
          CSV
        </button>
        <button type='button' className='emenda-relatorios-btn' onClick={handleExportTxt}>
          <FontAwesomeIcon icon={faFileAlt} className='mr-1' />
          TXT
        </button>
      </div>

      {visitedTabs.relacao && (
        <div style={{ display: activeTab === 'relacao' ? '' : 'none' }}>
          <RelacaoEmendasTab
            identificador={identificador}
            service={service}
            page={page}
            setPage={setPage}
            filtros={filtrosAplicados}
            onTotalizadoresChange={handleTotalizadoresChange}
          />
        </div>
      )}
      {visitedTabs.execucao && (
        <div style={{ display: activeTab === 'execucao' ? '' : 'none' }}>
          <ExecucaoDespesaTab
            identificador={identificador}
            filtros={filtrosAplicados}
            onReportChange={(report) => handleTabReportChange('execucao', report)}
          />
        </div>
      )}
      {visitedTabs.receitas && (
        <div style={{ display: activeTab === 'receitas' ? '' : 'none' }}>
          <ReceitasTab
            identificador={identificador}
            filtros={filtrosAplicados}
            onReportChange={(report) => handleTabReportChange('receitas', report)}
          />
        </div>
      )}
      {visitedTabs.ajustes && (
        <div style={{ display: activeTab === 'ajustes' ? '' : 'none' }}>
          <AjustesTab
            identificador={identificador}
            filtros={filtrosAplicados}
            onReportChange={(report) => handleTabReportChange('ajustes', report)}
          />
        </div>
      )}
      {visitedTabs.liquidacao && (
        <div style={{ display: activeTab === 'liquidacao' ? '' : 'none' }}>
          <LiquidacaoTab
            identificador={identificador}
            filtros={filtrosAplicados}
            onReportChange={(report) => handleTabReportChange('liquidacao', report)}
          />
        </div>
      )}
      {visitedTabs.pagamentos && (
        <div style={{ display: activeTab === 'pagamentos' ? '' : 'none' }}>
          <PagamentosTab
            identificador={identificador}
            filtros={filtrosAplicados}
            onReportChange={(report) => handleTabReportChange('pagamentos', report)}
          />
        </div>
      )}
    </Container>
  );
}
